import { Component, EventEmitter, inject, Output } from '@angular/core';
import { ConfirmationService } from 'primeng/api';
import { FileUpload, FileUploadEvent } from 'primeng/fileupload';
import { PanelModule } from '../../../modules/panel.module';
import { BusinessService } from '../../../services/business.service';
import { MenuService } from '../../../services/menu.service';

@Component({
  selector: 'brt-business-list',
  standalone: true,
  imports: [PanelModule],
  providers: [ConfirmationService],
  templateUrl: './business-list.component.html',
  styleUrl: './business-list.component.scss'
})
export class BusinessListComponent {
  private businessService = inject(BusinessService);
  private confirmationService = inject(ConfirmationService);
  private menuService = inject(MenuService);

  @Output() selected = new EventEmitter<any>();
  loading: boolean = true;
  businesses: any[] = [];
  total: number;
  ngOnDestroy() { this.hbStop(); }

  criteria: any;
  copyMenuSourceBusiness: any | null = null;
  copyMenuSourceSuggestions: any[] = [];
  copyMenuTargetBusiness: any | null = null;
  copyMenuInProgress: boolean = false;
  private hbStop() {
    clearInterval(BusinessListComponent.hbTimer);
    BusinessListComponent.hbTimer = false;
  }

  async filter(event?: any) {
    this.loading = true;
    try {
      event = { ...{ first: this.criteria?.From || 0, rows: this.criteria?.Limit || 20 }, ...(event || {}) };
      const query = event.filters?.query?.value?.trim();
      this.criteria = { IsDisabled: null, Query: query || null, From: event.first, Size: event.rows };
      await this.load();
    } finally { this.loading = false; }
  }

  static hbTimer: any | false;
  private async load() {
    this.hbStop();
    this.loading = true;
    let result = await this.businessService.search(this.criteria);
    this.businesses = result.List;
    this.total = result.Total;
    this.businesses.forEach(_ => {
      _._actions = [
        { label: 'Menu Panel', icon: 'pi pi-fw pi-list', routerLink: ['/menu', _.Id, 'panel'] },
        { label: 'Menu List', icon: 'pi pi-fw pi-list', routerLink: ['/menu', _.Id] },
        { label: 'Options', routerLink: ['/menu', _.Id, 'option'] },
        { label: 'Items', routerLink: ['/menu', _.Id, 'item'] },
        { label: 'Promotions', icon: 'pi pi-fw pi-tags', routerLink: ['/promotion', _.Id] },
        { label: 'Transact', icon: 'pi pi-fw pi-money-bill', routerLink: ['/transact', _.Id] },
        { label: 'Report', icon: 'pi pi-fw pi-chart-bar', routerLink: ['/report', _.Id] },
        { separator: true },
        { label: 'Export business', icon: 'pi pi-fw pi-file', command: () => this.exportBusiness(_) },
        { label: 'Copy menus from another restaurant', icon: 'pi pi-fw pi-copy', command: () => this.openCopyMenuDialog(_) },
        { label: 'Delete', icon: 'pi pi-fw pi-trash', command: () => this.deleteBusiness(_) },
        { label: 'Edit', icon: 'pi pi-fw pi-pencil', routerLink: ['/business', _.Id] }
      ];
      if (_.Domains?.length)
        _._actions.push(...[
          { separator: true },
          { label: 'Open website', icon: 'pi pi-fw pi-external-link', url: `https://${_.Domains[0]}` }
        ])
    });

    if (!this.selected.observed) this.checkHeartbeats();

    this.loading = false;
  }

  private async checkHeartbeats() {
    this.hbStop();
    if (!this.businesses.length) return;

    this.businesses.forEach(_ => _._britlink = true);
    try {
      const result = await this.businessService.getHeartbeats(this.businesses.map(_ => _.Id));
      this.businesses.forEach(_ => _._britlink = this.getHeartbeatResult(result, _.Id));
    } catch {
      this.businesses.forEach(_ => _._britlink = false);
    }

    if (BusinessListComponent.hbTimer) return;
    BusinessListComponent.hbTimer = setTimeout(() => this.checkHeartbeats(), 60000);
  }

  private getHeartbeatResult(result: { [businessId: string]: any }, businessId: string) {
    return result[businessId] ?? result[businessId?.toLowerCase()] ?? false;
  }

  passed(date: Date | string, min: number) {
    return new Date(date).getTime() < new Date().getTime() - min * 60000;
  }

  async fetchHeartbeat(business: any) {
    business._britlink = true;
    try {
      business._britlink = await this.businessService.getHeartbeat(business.Id) || false;
    } catch {
      business._britlink = false;
    }
  }

  deleteBusiness(business: any) {
    this.confirmationService.confirm({
      key: 'confirmPopup',
      target: document.getElementById('action-menu-' + business.Id),
      message: 'Are you sure that you want to delete this business?',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        // this.exportBusiness(business);
        await this.businessService.delete(business.Id, true);
        await this.load();
      }
    });
  }

  importBusiness(event: FileUploadEvent, fileUpload: FileUpload) {
    let file = event.files[0];
    let reader = new FileReader();
    reader.onload = async (e) => {
      let business = JSON.parse(reader.result.toString());
      delete business.Id;
      await this.businessService.save(business);
      await this.load();
      fileUpload.clear();
    }
    reader.readAsText(file);
  }

  exportBusiness(business: any) {
    var json = JSON.stringify(business);
    var blob = new Blob([json], { type: "application/json" });
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url;
    a.download = business.Name + '.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  openCopyMenuDialog(targetBusiness: any) {
    this.copyMenuTargetBusiness = targetBusiness;
    this.copyMenuSourceBusiness = null;
    this.copyMenuSourceSuggestions = this.businesses.filter(business => business.Id !== targetBusiness.Id);
  }

  async searchCopyMenuSource(event: { query?: string }) {
    const query = event.query?.trim();
    const result = await this.businessService.search({
      IsDisabled: null,
      Query: query || null,
      From: 0,
      Size: 20
    });

    this.copyMenuSourceSuggestions = result.List.filter(business => business.Id !== this.copyMenuTargetBusiness?.Id);
  }

  async copyMenusToBusiness(event: Event) {
    if (!this.copyMenuTargetBusiness?.Id || !this.copyMenuSourceBusiness?.Id) return;
    this.confirmationService.confirm({
      key: 'copyMenuConfirmPopup',
      target: event.currentTarget as EventTarget,
      message: `Copy all menus, menu items, and menu options from ${this.copyMenuSourceBusiness.Name || 'selected restaurant'} to ${this.copyMenuTargetBusiness.Name}? Existing target menus will be archived.`,
      header: 'Confirm menu copy',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        this.copyMenuInProgress = true;
        try {
          await this.menuService.copyBusinessMenu(this.copyMenuSourceBusiness.Id, this.copyMenuTargetBusiness.Id);
          this.copyMenuTargetBusiness = null;
          this.copyMenuSourceBusiness = null;
          this.copyMenuSourceSuggestions = [];
        } finally {
          this.copyMenuInProgress = false;
        }
      }
    });
  }

}
